'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { AIModel } from './CreateGameScreen';

const promptExamples = [
  { id: 'ex-1', text: 'Cria um jogo de plataformas 2D com um ninja que tem de evitar armadilhas e chegar ao fim do nível' },
  { id: 'ex-2', text: 'Cria um jogo de sobrevivência espacial onde tenho de fugir de meteoros e asteroides' },
  { id: 'ex-3', text: 'Cria um jogo de corrida futurista com nave espacial em túneis de néon' },
  { id: 'ex-4', text: 'Cria um jogo de defesa de torre com zumbis que atacam a minha base' },
  { id: 'ex-5', text: 'Cria um RPG de texto onde exploro uma masmorra e luto contra monstros' },
  { id: 'ex-6', text: 'Cria um puzzle de combinar peças coloridas com sistema de pontuação' },
];

interface PromptAreaProps {
  prompt: string;
  setPrompt: (v: string) => void;
  onGenerate: () => void;
  model: AIModel;
  canGenerate: boolean;
  dailyCredits: number;
  purchasedCredits: number;
}

export default function PromptArea({
  prompt,
  setPrompt,
  onGenerate,
  model,
  canGenerate,
  dailyCredits,
  purchasedCredits,
}: PromptAreaProps) {
  const [showHistory, setShowHistory] = useState(false);

  const promptHistory = [
    { id: 'hist-1', text: 'Jogo de plataformas com personagem ninja', date: 'Há 2 horas' },
    { id: 'hist-2', text: 'Space shooter com asteroides e power-ups', date: 'Ontem' },
    { id: 'hist-3', text: 'Puzzle de combinar 3 peças iguais', date: '07/09/2026' },
  ];

  const creditsUsed = dailyCredits >= model.creditsPerGeneration
    ? { daily: model.creditsPerGeneration, purchased: 0 }
    : { daily: dailyCredits, purchased: model.creditsPerGeneration - dailyCredits };

  return (
    <div className="space-y-4">
      {/* Main prompt card */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden glow-card">
        <div className="p-5 pb-3">
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="game-prompt" className="text-sm font-semibold text-foreground">
              Descreve o teu jogo
            </label>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ClockIcon" size={13} />
              Histórico
            </button>
          </div>

          <textarea
            id="game-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Descreve o jogo que queres criar... Quanto mais detalhes deres, melhor será o resultado.

Exemplos:
• 'Cria um jogo de plataformas 2D com um personagem que tem de chegar ao fim do nível'
• 'Cria um jogo de corrida futurista com física realista' •'Cria um RPG de texto numa masmorra com sistema de inventário'"
            className="w-full h-52 bg-input border border-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none leading-relaxed rounded-lg p-2"
          />
        </div>

        {/* Bottom bar */}
        <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="tabular-nums">{prompt.length} / 2000</span>
            <span className="flex items-center gap-1">
              <Icon name="BoltIcon" size={11} className="text-warning" />
              {model.creditsPerGeneration} créditos
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-muted/50 transition-colors" title="Anexar referência">
              <Icon name="PaperClipIcon" size={16} className="text-muted-foreground" />
            </button>
            <button
              onClick={onGenerate}
              disabled={!prompt.trim() || !canGenerate}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="SparklesIcon" size={15} variant="solid" />
              Criar jogo
            </button>
          </div>
        </div>
      </div>

      {/* Credit cost breakdown */}
      <div className="flex flex-wrap items-center gap-3 px-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon name="BoltIcon" size={13} className="text-warning" />
          <span>Custo estimado:</span>
          <span className="font-semibold text-foreground tabular-nums">{model.creditsPerGeneration} créditos</span>
        </div>
        {creditsUsed.daily > 0 && (
          <span className="text-xs text-muted-foreground">
            {creditsUsed.daily} diários
            {creditsUsed.purchased > 0 && ` + ${creditsUsed.purchased} comprados`}
          </span>
        )}
        {!canGenerate && (
          <span className="text-xs text-danger font-medium flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            Créditos insuficientes
          </span>
        )}
      </div>

      {/* Prompt history dropdown */}
      {showHistory && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden animate-slide-up">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Prompts anteriores</p>
          </div>
          <ul>
            {promptHistory.map((h) => (
              <li key={h.id}>
                <button
                  onClick={() => { setPrompt(h.text); setShowHistory(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                >
                  <Icon name="ClockIcon" size={14} className="text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{h.text}</p>
                    <p className="text-xs text-muted-foreground">{h.date}</p>
                  </div>
                  <Icon name="ArrowUpLeftIcon" size={13} className="text-muted-foreground flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Example chips */}
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-3">Experimenta um exemplo:</p>
        <div className="flex flex-wrap gap-2">
          {promptExamples.map((ex) => (
            <button
              key={ex.id}
              onClick={() => setPrompt(ex.text)}
              className="text-xs px-3 py-1.5 rounded-full bg-muted/50 border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/5 transition-all duration-150 truncate max-w-xs"
            >
              {ex.text.substring(0, 50)}{ex.text.length > 50 ? '...' : ''}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}